import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import NodeCache from 'node-cache'
import { Observable } from 'rxjs'
import { take } from 'rxjs/operators'
import {
  ExternalRawData,
  FileData,
  SleeveAggAccount,
} from '../models/external-file-viewer'

const EXTERNAL_RAW_DATA_HOST = 'helios-external-raw-data'
const EXTERNAL_RAW_DATA_FILES_URL = `${EXTERNAL_RAW_DATA_HOST}/file`
const EXTERNAL_RAW_DATA_FILES_BY_ID_URL = `${EXTERNAL_RAW_DATA_HOST}/file/`
const AGGREGATE_SUBACCOUNTS_URL = `${EXTERNAL_RAW_DATA_HOST}/account/subAccountMap/`
const EXTERNAL_RAW_DATA_FOR_CW_TRANS_URL = `${EXTERNAL_RAW_DATA_HOST}/file/tranId/`
const EXTERNAL_RAW_DATA_FOR_EXT_TRANS_URL = `${EXTERNAL_RAW_DATA_HOST}/file/originId/`

@Injectable({
  providedIn: 'root',
})
export class ExternalFileViewerService {
  private readonly fetchFileByIdCache = new NodeCache({
    forceString: false,
    stdTTL: 600, //10 Minutes
    checkperiod: 60,
    errorOnMissing: false,
    useClones: false,
    deleteOnExpire: true,
  })

  constructor(private readonly httpClient: HttpClient) {}

  fetchRawData(
    startReconDate: string,
    endReconDate: string,
    accountIds: number[],
  ): Observable<ExternalRawData[]> {
    let params = new HttpParams()
    params = params.append('startReconDate', startReconDate)
    params = params.append('endReconDate', endReconDate)
    accountIds.forEach(aids => {
      params = params.append('aids', aids)
    })

    const options = {
      params,
    }
    return this.httpClient.get<ExternalRawData[]>(
      `${EXTERNAL_RAW_DATA_FILES_URL}`,
      options,
    )
  }

  async fetchFileById(
    fileId: number,
    startReconDate: string,
    endReconDate: string,
    accountId: number,
  ): Promise<FileData> {
    const cacheKey = `${fileId}-${startReconDate}-${endReconDate}-${accountId}`

    let fileData = this.fetchFileByIdCache.get<FileData>(cacheKey)
    if (fileData != null) {
      return Promise.resolve(fileData)
    }

    let params = new HttpParams()
    params = params.append('startReconDate', startReconDate)
    params = params.append('endReconDate', endReconDate)
    params = params.append('accountId', accountId)
    const options = {
      params,
    }

    fileData = await this.httpClient
      .get<FileData>(`${EXTERNAL_RAW_DATA_FILES_BY_ID_URL}` + fileId, options)
      .pipe(take(1))
      .toPromise()

    this.fetchFileByIdCache.set<FileData>(cacheKey, fileData)

    return fileData
  }

  fetchSubAccounts(accountId: number): Observable<SleeveAggAccount[]> {
    return this.httpClient.get<SleeveAggAccount[]>(
      `${AGGREGATE_SUBACCOUNTS_URL}` + accountId,
    )
  }

  fetchRawDataForCwTransaction(tranId: string): Observable<ExternalRawData[]> {
    return this.httpClient.get<ExternalRawData[]>(
      `${EXTERNAL_RAW_DATA_FOR_CW_TRANS_URL}` + tranId,
    )
  }

  fetchRawDataForExtTransaction(
    originId: string,
    accountId: number,
    reconDate: string,
  ): Observable<ExternalRawData[]> {
    let params = new HttpParams()
    params = params.append('reconDate', reconDate)
    params = params.append('accountId', accountId)
    const options = {
      params,
    }
    return this.httpClient.get<ExternalRawData[]>(
      `${EXTERNAL_RAW_DATA_FOR_EXT_TRANS_URL}` + originId,
      options,
    )
  }
}
